import { useEffect, useState } from 'react';
import { worldUrl } from './version';
// World assets: the floors, overlays and per-person atlases written by
// frontend/art/build_assets.py into public/world/. Everything here is a
// cache keyed by URL; nothing blocks the UI, a missing image just draws
// nothing until it arrives.

export type FloorId = 'f1' | 'g' | 'l3' | 'h5' | 'ext';

export interface RoomMeta {
  floor: FloorId;
  /** x, y, w, h in floor pixels. */
  rect: [number, number, number, number];
  label: string;
  /** Wider rect lit for the room's screen (the Great Hall lights its stair halls too). */
  lit?: [number, number, number, number];
}

export interface ConfSeat { kind: 'far' | 'near' | 'end_l' | 'end_r'; x: number; top: number; card: [number, number] }

export interface WorldMeta {
  version: number;
  map: { w: number; h: number; floorOrigin: [number, number] };
  floors: Record<FloorId, { img: string; w: number; h: number; at: [number, number]; label: string }>;
  rooms: Record<string, RoomMeta>;
  overlays: Record<'bench' | 'conf' | 'desk', { img: string; at: [number, number] }>;
  /** Bench seats viewer's left to right: [x, desk top row]. */
  seats: Array<[number, number]>;
  /** Conference seats by seniority position (0 = the Chief). */
  conf: ConfSeat[];
  desk: [number, number];
  lectern: [number, number];
  lamps: [number, number];
  counsel: Record<string, [number, number]>;
  people: string[];
  era: Record<string, string>;
  props: { img: string; frames: Record<string, [number, number, number, number]> };
  /** People who live in the rooms (M10): drawn on every floor at runtime, see life.ts. */
  cast?: CastEntry[];
  /** Moving things: clocks, fountains, flags, trees, light, lamps, steam, drapes, pigeons. See fx.ts. */
  fx?: FxEntry[];
  /** Objects the UI sits on, recorded by the art code: [x, y, w, h, floor] in map pixels (cast.anchor in Python). */
  anchors?: Record<string, [number, number, number, number, FloorId]>;
}

/** A waypoint: x, y, pause in ms, and an optional view to hold while paused or 'door' (out of sight while paused). */
export type Waypoint = [number, number, number?, string?];

export interface CastEntry {
  id: string;
  floor: FloorId;
  room: string;
  person: string;
  kind: 'desk' | 'back' | 'side' | 'stand' | 'walk';
  x: number;
  y: number;
  life: string[];
  pose?: string;
  view?: 'front' | 'back' | 'l' | 'r';
  views?: Array<'front' | 'back' | 'l' | 'r'>;
  facing?: 'l' | 'r';
  path?: Waypoint[];
  speed?: number;
  clip?: number;
  with?: string;
  face?: string;
  look?: number;
  /** Arms for a standing extra: hold (a paper in front), point, point_l. */
  arms?: string;
}

export interface FxEntry {
  kind: 'clock' | 'fountain' | 'flag' | 'tree' | 'light' | 'lamp' | 'steam' | 'drape' | 'pigeons' | 'water' | string;
  floor: FloorId;
  x: number;
  y: number;
  r?: number;
  w?: number;
  h?: number;
  v?: number;
  n?: number;
}

/** [x, y, w, h, anchorX, anchorY] inside the atlas sheet. */
export type Frame = [number, number, number, number, number, number];

export interface Atlas {
  id: string;
  img: HTMLImageElement | null;
  frames: Record<string, Frame>;
  /** Head origin relative to the frame anchor, per frame set (bench.folded, stand.front, bust...). */
  head: Record<string, [number, number]>;
}

let metaPromise: Promise<WorldMeta> | null = null;
let metaValue: WorldMeta | null = null;
const images = new Map<string, HTMLImageElement>();
const atlases = new Map<string, Atlas>();
const atlasLoads = new Map<string, Promise<Atlas | null>>();
const listeners = new Set<() => void>();

/** Called whenever an image or atlas finishes loading, so the stage redraws. */
export function onAssetLoad(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
function notify(): void { listeners.forEach((fn) => fn()); }

export function canLoadImages(): boolean {
  return typeof window !== 'undefined' && typeof Image !== 'undefined'
    && !(typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent));
}

export function loadMeta(): Promise<WorldMeta> {
  if (!metaPromise) {
    metaPromise = fetch(worldUrl('world.json'))
      .then((r) => {
        if (!r.ok) throw new Error(`world.json ${r.status}`);
        return r.json() as Promise<WorldMeta>;
      })
      .then((m) => { metaValue = m; notify(); return m; });
    metaPromise.catch(() => { metaPromise = null; });
  }
  return metaPromise;
}

export function meta(): WorldMeta | null { return metaValue; }

/** Test hook: install world metadata without a fetch. */
export function setMetaForTests(m: WorldMeta | null): void {
  metaValue = m;
  metaPromise = m ? Promise.resolve(m) : null;
}

/** The image if loaded, else null (and a load is started). */
export function image(name: string): HTMLImageElement | null {
  const url = worldUrl(name);
  const got = images.get(url);
  if (got) return got.complete && got.naturalWidth > 0 ? got : null;
  if (!canLoadImages()) return null;
  const im = new Image();
  im.decoding = 'async';
  im.onload = notify;
  im.src = url;
  images.set(url, im);
  return null;
}

/** Atlas file name for a justice on a given case: era variants before 2016. */
export function personFile(id: string, arguedYear?: number | null): string {
  const m = metaValue;
  if (m && arguedYear && arguedYear < 2016 && m.era[id]) return m.era[id];
  return id;
}

export function atlas(file: string): Atlas | null {
  const got = atlases.get(file);
  if (got) return got.img ? got : null;
  if (!atlasLoads.has(file) && canLoadImages()) {
    // The sheet downloads beside its frame table, not after it: one round trip
    // per person instead of two (a phone on slow 4G paid ~0.2 s for each).
    const im = new Image();
    const loaded = new Promise<boolean>((resolve) => {
      im.onload = () => resolve(true);
      im.onerror = () => resolve(false);
    });
    im.src = worldUrl(`people/${file}.webp`);
    const p = fetch(worldUrl(`people/${file}.json`))
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { id: string; frames: Record<string, Frame>; head: Record<string, [number, number]> } | null) => {
        if (!j) return null;
        const a: Atlas = { id: j.id, img: null, frames: j.frames, head: j.head };
        atlases.set(file, a);
        void loaded.then((ok) => { if (ok) { a.img = im; notify(); } });
        return a;
      })
      .catch(() => null);
    atlasLoads.set(file, p);
  }
  return null;
}

/** Every floor's art, fetched once the first room is drawn, so a lift ride never
 * waits on the next floor's download (the first floor's sheet is 354 KB, about
 * two seconds on a slow phone). Five files; the residents' sheets are small and
 * load with their floor (prefetching all of them was ~190 requests). */
export function prefetchBuilding(): void {
  const m = metaValue;
  if (!m || !canLoadImages()) return;
  image(m.props.img);
  // Where a player goes next first: the docket (ground floor), then the
  // courtroom's floor, then the rest.
  const order = (id: string) => (id === 'g' ? 0 : id === 'f1' ? 1 : 2);
  const ids = (Object.keys(m.floors) as FloorId[]).sort((a, b) => order(a) - order(b));
  for (const id of ids) image(m.floors[id].img);
}

/** Start loading a set of atlases ahead of need (the bench for a case). */
export function preloadPeople(files: string[]): void {
  files.forEach((f) => { atlas(f); });
}

/** React: the world metadata, re-rendering once it has loaded. */
export function useWorldMeta(): WorldMeta | null {
  const [, force] = useState(0);
  useEffect(() => {
    if (!metaValue && canLoadImages()) loadMeta().then(() => force((n) => n + 1)).catch(() => {});
    return undefined;
  }, []);
  return metaValue;
}

/** React: a person's atlas, re-rendering when it arrives (null until then, and always in tests). */
export function useAtlas(file: string | null): Atlas | null {
  const [, force] = useState(0);
  const a = file ? atlas(file) : null;
  useEffect(() => {
    if (!file || a) return undefined;
    return onAssetLoad(() => { if (atlas(file)) force((n) => n + 1); });
  }, [file, a]);
  return a;
}
